import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { uploadDocument, startAudit } from '../services/api';
import { useAuth } from '../context/AuthContext';

const SERVICE_TYPES = {
  home_health: {
    label: 'Home Health',
    description: 'OASIS, ADR, POC, 485 audits',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
      </svg>
    ),
    docTypes: [
      { value: 'adr', label: 'ADR (Additional Development Request)' },
      { value: 'oasis', label: 'OASIS Assessment' },
      { value: 'poc_485', label: '485 Plan of Care' },
      { value: 'physician_orders', label: 'Physician Orders' },
      { value: 'visit_notes', label: 'Visit Notes (SN/PT/OT/ST)' },
      { value: 'recertification', label: 'Recertification' },
      { value: 'discharge', label: 'Discharge Summary' },
      { value: 'other_hh', label: 'Other Home Health Document' },
    ],
  },
  hospice: {
    label: 'Hospice',
    description: 'CTI, IDG, Election, GIP audits',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    docTypes: [
      { value: 'cti', label: 'CTI (Certificate of Terminal Illness)' },
      { value: 'election', label: 'Hospice Election Statement' },
      { value: 'idg_notes', label: 'IDG (Interdisciplinary Group) Notes' },
      { value: 'f2f_hospice', label: 'Face-to-Face Encounter' },
      { value: 'hospice_poc', label: 'Hospice Plan of Care' },
      { value: 'recert_hospice', label: 'Hospice Recertification' },
      { value: 'gip', label: 'GIP (General Inpatient Care) Records' },
      { value: 'continuous_care', label: 'Continuous Home Care Records' },
      { value: 'visit_notes_hospice', label: 'Visit Notes (RN/SW/Chaplain/Aide)' },
      { value: 'discharge_hospice', label: 'Discharge / Revocation' },
      { value: 'other_hospice', label: 'Other Hospice Document' },
    ],
  },
};

export default function Upload() {
  const { user } = useAuth();
  const isConsultant = user?.accountType === 'consultant';

  const [serviceType, setServiceType] = useState('');
  const [fileEntries, setFileEntries] = useState([]);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState([]);
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    if (!serviceType) {
      setError('Please select Home Health or Hospice before uploading files');
      return;
    }
    setError('');
    const newEntries = acceptedFiles.map(file => ({
      file,
      clientAgencyName: isConsultant ? '' : (user?.agencyName || ''),
      patientName: '',
      patientRef: '',
      documentType: '',
    }));
    setFileEntries(prev => [...prev, ...newEntries]);
  }, [isConsultant, user?.agencyName, serviceType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv'],
    },
    maxSize: 50 * 1024 * 1024,
  });

  const updateEntry = (index, field, value) => {
    setFileEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    ));
  };

  const removeEntry = (index) => {
    setFileEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadAll = async () => {
    if (!serviceType) return setError('Please select Home Health or Hospice');
    if (fileEntries.length === 0) return setError('Please add at least one file');

    if (isConsultant) {
      const missing = fileEntries.some(e => !e.clientAgencyName.trim());
      if (missing) return setError('Please enter a Client Agency Name for every file');
    }

    setError('');
    setProcessing(true);
    setProgress(fileEntries.map(() => ({ status: 'pending', message: 'Waiting...' })));

    const auditIds = [];

    for (let i = 0; i < fileEntries.length; i++) {
      const entry = fileEntries[i];

      setProgress(prev => prev.map((p, idx) =>
        idx === i ? { status: 'uploading', message: 'Uploading...' } : p
      ));

      try {
        const formData = new FormData();
        formData.append('document', entry.file);
        if (entry.clientAgencyName) formData.append('clientAgencyName', entry.clientAgencyName);
        if (entry.patientName) formData.append('patientName', entry.patientName);
        if (entry.patientRef) formData.append('patientReference', entry.patientRef);
        formData.append('serviceType', serviceType);
        if (entry.documentType) formData.append('documentType', entry.documentType);

        const uploadRes = await uploadDocument(formData);
        const documentId = uploadRes.data.document.id;

        setProgress(prev => prev.map((p, idx) =>
          idx === i ? { status: 'auditing', message: 'ChartAuditer is analyzing your document against CMS standards...' } : p
        ));

        const auditRes = await startAudit({
          documentId,
          clientAgencyName: entry.clientAgencyName,
          patientName: entry.patientName,
          patientReference: entry.patientRef,
          serviceType,
          documentType: entry.documentType,
        });

        auditIds.push(auditRes.data.auditId);

        setProgress(prev => prev.map((p, idx) =>
          idx === i ? { status: 'done', message: 'Audit started!' } : p
        ));
      } catch (err) {
        setProgress(prev => prev.map((p, idx) =>
          idx === i ? { status: 'error', message: err.response?.data?.error || 'Failed' } : p
        ));
      }
    }

    setProcessing(false);
    if (auditIds.length === 1) {
      navigate(`/audits/${auditIds[0]}`, {
        state: { pendingAudit: { id: auditIds[0], status: 'processing' } },
      });
    } else if (auditIds.length > 1) {
      navigate('/audits');
    }
  };

  const currentDocTypes = serviceType ? SERVICE_TYPES[serviceType].docTypes : [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Upload & Audit</h1>
      <p className="text-gray-500 mb-6">
        {isConsultant
          ? 'Upload client agency documents for AI-powered compliance audit'
          : 'Upload clinical documents for AI-powered compliance audit'}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>
      )}

      {/* Step 1: Service Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Step 1: Select Service Type</label>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(SERVICE_TYPES).map(([key, config]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setServiceType(key);
                // Reset document types when switching
                setFileEntries(prev => prev.map(e => ({ ...e, documentType: '' })));
              }}
              className={`p-5 rounded-2xl border-2 text-left transition flex items-start space-x-4 ${
                serviceType === key
                  ? 'border-teal-500 bg-teal-50/70 shadow-md'
                  : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/30'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                serviceType === key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {config.icon}
              </div>
              <div>
                <p className={`font-bold text-lg ${serviceType === key ? 'text-teal-700' : 'text-gray-700'}`}>
                  {config.label}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">{config.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Upload Files (only show after service type selected) */}
      {serviceType && (
        <>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Step 2: Upload Documents</label>
          <div {...getRootProps()}
            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition
              ${isDragActive ? 'border-teal-500 bg-teal-50/70' : fileEntries.length > 0 ? 'border-green-400 bg-green-50/70' : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'}`}>
            <input {...getInputProps()} />
            <div>
              <div className="mb-3">
                <svg className={`w-12 h-12 mx-auto ${fileEntries.length > 0 ? 'text-green-400' : 'text-teal-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="font-semibold text-gray-700">
                {fileEntries.length > 0
                  ? `${fileEntries.length} file(s) added — drop more or click to add`
                  : `Drop your ${SERVICE_TYPES[serviceType].label} documents here, or click to select`}
              </p>
              <p className="text-sm text-gray-500 mt-1">Supports PDF, DOCX, and CSV — up to 50MB each — select multiple files at once</p>
            </div>
          </div>
        </>
      )}

      {/* File List with Per-File Fields */}
      {fileEntries.length > 0 && (
        <div className="mt-6 space-y-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Step 3: Enter Document Details</label>
          {fileEntries.map((entry, idx) => (
            <div key={idx} className="glass-card border border-teal-100/50 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">{entry.file.name}</p>
                    <p className="text-xs text-gray-400">{(entry.file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                {!processing && (
                  <button onClick={() => removeEntry(idx)}
                    className="text-red-400 hover:text-red-600 text-sm font-medium">Remove</button>
                )}
                {processing && progress[idx] && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    progress[idx].status === 'done' ? 'bg-green-100 text-green-700' :
                    progress[idx].status === 'error' ? 'bg-red-100 text-red-700' :
                    progress[idx].status === 'pending' ? 'bg-gray-100 text-gray-500' :
                    'bg-teal-100 text-teal-700 animate-pulse'
                  }`}>
                    {progress[idx].message}
                  </span>
                )}
              </div>

              {!processing && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Document Type Dropdown */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Document Type</label>
                    <select
                      value={entry.documentType}
                      onChange={(e) => updateEntry(idx, 'documentType', e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80"
                    >
                      <option value="">Select document type...</option>
                      {currentDocTypes.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Patient Full Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Patient Full Name</label>
                    <input value={entry.patientName}
                      onChange={(e) => updateEntry(idx, 'patientName', e.target.value)}
                      placeholder="e.g. John Smith"
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
                  </div>

                  {/* Client Agency Name (consultants only) */}
                  {isConsultant && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Client Agency Name *</label>
                      <input value={entry.clientAgencyName}
                        onChange={(e) => updateEntry(idx, 'clientAgencyName', e.target.value)}
                        placeholder="e.g. Sunrise Home Health"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
                    </div>
                  )}

                  {/* Chart Ref / MRN */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Chart Ref / MRN (optional)</label>
                    <input value={entry.patientRef}
                      onChange={(e) => updateEntry(idx, 'patientRef', e.target.value)}
                      placeholder="e.g. MRN-12345"
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bulk Apply Agency Name */}
      {isConsultant && fileEntries.length > 1 && !processing && (
        <div className="mt-4 bg-teal-50 border border-teal-200 rounded-xl p-3 flex items-center gap-3">
          <span className="text-xs text-teal-600 font-medium whitespace-nowrap">Apply to all:</span>
          <input
            placeholder="Set client agency name for all files"
            className="flex-1 px-3 py-1.5 text-sm border border-teal-300 rounded-xl focus:ring-2 focus:ring-teal-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.value.trim()) {
                const val = e.target.value.trim();
                setFileEntries(prev => prev.map(entry => ({ ...entry, clientAgencyName: val })));
                e.target.value = '';
              }
            }}
          />
          <span className="text-xs text-teal-400">Press Enter to apply</span>
        </div>
      )}

      {/* Submit Button */}
      {serviceType && (
        <button onClick={handleUploadAll}
          disabled={fileEntries.length === 0 || processing}
          className="mt-6 w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold py-3 rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition">
          {processing
            ? `Processing ${fileEntries.length} file(s)...`
            : `Upload & Start ${SERVICE_TYPES[serviceType].label} Audit${fileEntries.length > 1 ? ` (${fileEntries.length} files)` : ''}`}
        </button>
      )}
    </div>
  );
}
